import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Permission-gating foundation for the SPA (Phase C deliverable; consumed by the
 * Phase D customer UI).
 *
 * The auth payload carries the union of the user's permission strings (["*"] for
 * owner). These helpers let components hide/disable actions the user cannot take.
 * They are a UX layer only — the backend PermissionGuard remains the source of
 * truth and returns 403 regardless of what the UI renders.
 *
 * Usage:
 *   const can = useHasPermission();
 *   can('customer:delete')            // boolean
 *
 *   const { has, hasAny, hasAll } = usePermissions();
 *   has('customer:create')
 *   hasAny(['customer:update', 'customer:delete'])
 */
export function usePermissions() {
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);

  return useMemo(() => {
    const set = new Set(permissions);
    const has = (permission: string): boolean => set.has('*') || set.has(permission);
    return {
      permissions,
      has,
      hasAny: (perms: string[]): boolean => set.has('*') || perms.some((p) => set.has(p)),
      hasAll: (perms: string[]): boolean => set.has('*') || perms.every((p) => set.has(p)),
    };
  }, [permissions]);
}

/** Convenience: returns a `can(permission)` checker function. */
export function useHasPermission(): (permission: string) => boolean {
  const { has } = usePermissions();
  return has;
}
